/**
 * CombatTimerArc — chalk-stroked SVG arc draining clockwise.
 *
 * Renders an SVG circle whose stroke-dasharray is animated from "full"
 * to "empty" over `duration` seconds. When the arc empties we fire
 * `onExpire` exactly once.
 *
 * The arc is purely visual — server-side timer authority lives in
 * Stream A's session settings (`SessionSettings.turnTimer`) and the
 * actual expiry trigger is a server broadcast in production. For the
 * UI prototype the parent uses the local timer to short-circuit a
 * verdict.
 *
 * Honours `prefers-reduced-motion`: renders a static "remaining"
 * indicator (label only, no animation) and never auto-fires onExpire.
 */

import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { FILTER_IDS } from '../../filters';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface CombatTimerArcProps {
  /** Total countdown duration in seconds. Must be > 0. */
  duration: number;
  /** Fired exactly once when the arc empties. */
  onExpire: () => void;
  /** Pixel diameter of the arc. Default 56. */
  size?: number;
  /**
   * Subdue the visual register (lower opacity, non-pulsing). Used when
   * the timer is shown to a non-attacker so they understand the urgency
   * without thinking it's their clock to act on.
   */
  subdued?: boolean | undefined;
}

export function CombatTimerArc({ duration, onExpire, size = 56, subdued = false }: CombatTimerArcProps) {
  const reduced = useReducedMotion();
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const [remaining, setRemaining] = useState(duration);
  const fired = useRef(false);

  // Latest-callback ref so the countdown interval doesn't depend on
  // `onExpire`'s identity. Without this, every re-render that hands us a
  // fresh `onExpire` reference (e.g. parent re-renders after a learning-
  // answer broadcast or any gameState frame) tears the effect down,
  // restarting `start = performance.now()` and resetting the wall clock.
  // The visual SVG arc keeps draining via Motion's animation clock, so
  // the player sees the timer hit zero — but the JS interval is back at
  // t=0 and never fires `onExpire`, so TURN_TIMEOUT is never dispatched.
  // Fix: read the live callback through a ref inside the tick.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Tick the displayed remaining count every 250ms. Independent of the
  // SVG animation (which uses Motion's continuous interpolation) so the
  // numeric label feels like a sane wall clock. Effect deps deliberately
  // exclude `onExpire` (read via ref) — see comment above.
  useEffect(() => {
    if (reduced) return;
    fired.current = false;
    const start = performance.now();
    const id = window.setInterval(() => {
      const elapsed = (performance.now() - start) / 1000;
      const left = Math.max(0, duration - elapsed);
      setRemaining(left);
      if (left <= 0 && !fired.current) {
        fired.current = true;
        onExpireRef.current();
        window.clearInterval(id);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [duration, reduced]);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: fontStacks.mono,
        fontSize: 12,
        color: surfaces.graphite,
        letterSpacing: '0.08em',
        opacity: subdued ? 0.55 : 1,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
        style={{ filter: `url(#${FILTER_IDS.chalkRoughen})` }}
      >
        {/* Backing track — faint chalk circle. */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={surfaces.graphite}
          strokeWidth={2}
          opacity={0.2}
        />
        {/* Active arc — drains from full to empty, drawn via dashoffset. */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={surfaces.waxRed}
          strokeWidth={2.4}
          strokeLinecap="round"
          strokeDasharray={circumference}
          // Begin at 0 dashoffset (full ring) and end at full circumference
          // (empty ring). 12 o'clock start via -90deg rotation.
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          initial={{ strokeDashoffset: 0 }}
          animate={reduced ? { strokeDashoffset: 0 } : { strokeDashoffset: circumference }}
          transition={reduced ? { duration: 0 } : { duration, ease: 'linear' }}
        />
      </svg>
      <span>{Math.ceil(remaining)}s</span>
    </div>
  );
}
